import React, { FC } from 'react';
import { ui } from 'util/ui';

/**
 * Shared pieces for every Admin tab (UI refresh, 5 Nov 2026), matching the Settings boxes:
 * rounded white panels, plain buttons, status pills, and tables whose rows stack into small cards
 * on a phone (see .archy-trow in index.css).
 */

export const Panel: FC<{ padding?: number | string; style?: React.CSSProperties }> = ({ padding = 0, style, children }) => (
  <div style={{ background: ui.surface, border: `1px solid ${ui.line}`, borderRadius: 16, overflow: 'hidden', padding, fontFamily: ui.font, color: ui.ink, ...style }}>
    {children}
  </div>
);

export const SectionHead: FC<{ title: string; note?: React.ReactNode }> = ({ title, note, children }) => (
  <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '6px 12px', margin: '0 0 12px', fontFamily: ui.font }}>
    <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: ui.ink }}>{title}</h2>
    {note && <span style={{ fontSize: 13, color: ui.muted }}>{note}</span>}
    <span style={{ flex: 1 }} />
    {children}
  </div>
);

type Tone = 'plain' | 'primary' | 'danger';
export const AdminButton: FC<{ onClick(): void; disabled?: boolean; tone?: Tone; title?: string }> = ({
  onClick,
  disabled,
  tone = 'plain',
  title,
  children,
}) => (
  <button
    type="button"
    className="archy-btn"
    onClick={onClick}
    disabled={disabled}
    title={title}
    style={{
      height: 32,
      padding: '0 12px',
      borderRadius: 9,
      border: `1px solid ${tone === 'primary' ? ui.accent : tone === 'danger' ? '#E8B4B0' : ui.control}`,
      background: tone === 'primary' ? ui.accent : ui.surface,
      color: tone === 'primary' ? '#FFFFFF' : tone === 'danger' ? '#B3261E' : ui.ink,
      fontFamily: 'inherit',
      fontSize: 13,
      fontWeight: tone === 'primary' ? 600 : 500,
      cursor: disabled ? 'not-allowed' : 'pointer',
      opacity: disabled ? 0.45 : 1,
      whiteSpace: 'nowrap',
    }}
  >
    {children}
  </button>
);

export const Pill: FC<{ tone: 'green' | 'grey' | 'amber' }> = ({ tone, children }) => {
  const c = tone === 'green' ? ['#0B6B3F', '#E3F6EC'] : tone === 'amber' ? ['#7A5300', '#FEF4D8'] : ['#5B616E', '#F1F2F4'];
  return (
    <span style={{ display: 'inline-block', fontSize: 12, fontWeight: 600, color: c[0], background: c[1], borderRadius: 999, padding: '2px 10px', whiteSpace: 'nowrap' }}>
      {children}
    </span>
  );
};

/** A table row on a CSS grid. On a phone the cells stack and each shows its label. */
export const TRow: FC<{ columns: string; head?: boolean; style?: React.CSSProperties; onOpen?(): void; title?: string }> = ({
  columns,
  head,
  style,
  onOpen,
  title,
  children,
}) => (
  <div
    className={`archy-trow${head ? ' archy-thead' : ''}${onOpen ? ' archy-row-btn' : ''}`}
    style={{ gridTemplateColumns: columns, cursor: onOpen ? 'pointer' : undefined, ...style }}
    role={onOpen ? 'button' : undefined}
    tabIndex={onOpen ? 0 : undefined}
    title={title}
    onClick={onOpen}
    onKeyDown={onOpen ? (e) => e.key === 'Enter' && onOpen() : undefined}
  >
    {children}
  </div>
);

export const Cell: FC<{ label?: string; align?: 'left' | 'right'; muted?: boolean; style?: React.CSSProperties }> = ({
  label,
  align = 'left',
  muted,
  style,
  children,
}) => (
  <div className="archy-tcell" data-label={label} style={{ minWidth: 0, textAlign: align, color: muted ? ui.muted : undefined, fontSize: muted ? 13 : undefined, ...style }}>
    {children}
  </div>
);

export const Notice: FC<{ tone?: 'error' | 'info' }> = ({ tone = 'info', children }) => (
  <div
    role={tone === 'error' ? 'alert' : 'status'}
    style={{
      margin: '0 0 14px',
      padding: '12px 14px',
      borderRadius: 12,
      fontSize: 14,
      background: tone === 'error' ? '#FDECEA' : '#EEF4FB',
      color: tone === 'error' ? '#8C1D18' : '#1E4E8C',
      border: `1px solid ${tone === 'error' ? '#F3C7C3' : '#C9DCF2'}`,
      fontFamily: ui.font,
    }}
  >
    {children}
  </div>
);
